import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlanElement, PlanPlatz } from "@shared/plan";
import { api, rufe } from "../api/client";
import type { HubStatus } from "../../worker/durable/LadenHub";
import type { Kandidat, ScanErgebnis, WarenkorbAnsicht } from "../../worker/data/kasse";

export type { Kandidat, ScanErgebnis, WarenkorbAnsicht, HubStatus };

export interface GeraetDaten {
  /** E10.3: Benutzer-IDs, die heute laut Schichtplan eingeteilt sind. */
  eingeteilt?: string[];
  /** E11.2: Code für das Kundendisplay (/display/:code). */
  displayCode?: string;
  /** E14.2: laufendes Event (Rabatt des Ladens, Sonderprovision). */
  event?: { id: string; name: string; rabattProzent: number | null; provisionProzent: number | null; bis: string } | null;
  geraet: { id: string; name: string; art: "tablet" | "handy"; scannerArt: "kamera" | "bluetooth" } | null;
  laden?: { id: string; name: string; slug: string; logo: boolean; sumupVerbunden: boolean };
  personen?: { id: string; name: string; rolle: "inhaber" | "mitarbeiter" | "aushilfe"; pinGesetzt: boolean }[];
  kassierer?: { id: string; name: string; rolle: string; rechte: string[] } | null;
  einstellungen?: { sperreNachMinuten: number; scanTon: boolean; rabattFreigabeAbProzent: number };
  hub?: HubStatus;
}

export const KEY_GERAET = ["kasse", "geraet"] as const;
export const KEY_WARENKORB = ["kasse", "warenkorb"] as const;

export const useKassePlan = (offen: boolean) => useQuery({ queryKey: ["kasse", "plan"], queryFn: () => rufe<{ plan: { spalten: number; zeilen: number; hintergrundKey: string | null; elemente: PlanElement[] }; regale: { id: string; nummer: string; lage: string; platz: PlanPlatz | null }[] }>(api.api.kasse.plan.$get()), enabled: offen, staleTime: 300_000 });
export function useGeraet() {
  return useQuery({ queryKey: KEY_GERAET, queryFn: () => rufe<GeraetDaten>(api.api.kasse.geraet.$get()), staleTime: 30_000, refetchInterval: 60_000 });
}

export function useGeraetAnmelden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (d: { name: string; art: "tablet" | "handy"; scannerArt: "kamera" | "bluetooth" }) => rufe<{ geraetId: string }>(api.api.kasse.geraet.$post({ json: d })),
    onSuccess: () => qc.clear(),
  });
}

export function usePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (d: { benutzerId: string; pin: string }) => rufe(api.api.kasse.pin.$post({ json: d })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["kasse"] }),
  });
}

export function useSperren() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => rufe(api.api.kasse.sperren.$post()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["kasse"] }) });
}

export interface WarenkorbAntwort {
  warenkorb: WarenkorbAnsicht;
  kandidaten: { passend: Kandidat[]; andere: Kandidat[] } | null;
}

export function useWarenkorb(aktiv = true) {
  return useQuery({ queryKey: KEY_WARENKORB, queryFn: () => rufe<WarenkorbAntwort>(api.api.kasse.warenkorb.$get()), enabled: aktiv });
}

function wkMutation<TVars>(fn: (v: TVars) => Promise<Response>) {
  return () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (v: TVars) => rufe<WarenkorbAntwort & { ergebnis?: ScanErgebnis }>(fn(v)),
      onSuccess: (d) => {
        qc.setQueryData(KEY_WARENKORB, (alt: WarenkorbAntwort | undefined) => ({ warenkorb: d.warenkorb, kandidaten: d.kandidaten ?? alt?.kandidaten ?? null }));
      },
    });
  };
}

export const useScan = wkMutation((v: { id: string; code: string }) => api.api.kasse.warenkorb[":id"].scan.$post({ param: { id: v.id }, json: { code: v.code } }));
export const useLadenware = wkMutation((v: { id: string; bezeichnung: string; preisCent: number }) => api.api.kasse.warenkorb[":id"].ladenware.$post({ param: { id: v.id }, json: { bezeichnung: v.bezeichnung, preisCent: v.preisCent } }));
export const usePositionEntfernen = wkMutation((v: { id: string; pos: string }) => api.api.kasse.warenkorb[":id"].positionen[":pos"].$delete({ param: { id: v.id, pos: v.pos } }));
export const useFertig = wkMutation((v: { id: string }) => api.api.kasse.warenkorb[":id"].fertig.$post({ param: { id: v.id } }));
export const useZurueck = wkMutation((v: { id: string }) => api.api.kasse.warenkorb[":id"].zurueck.$post({ param: { id: v.id } }));
export const useAbbrechen = wkMutation((v: { id: string; grund?: string }) => api.api.kasse.warenkorb[":id"].abbrechen.$post({ param: { id: v.id }, json: { grund: v.grund ?? "" } }));
export const useZuordnen = wkMutation((v: { id: string; transactionCode: string; notiz?: string }) => api.api.kasse.warenkorb[":id"].zuordnen.$post({ param: { id: v.id }, json: { transactionCode: v.transactionCode, notiz: v.notiz ?? "" } }));
export const useSpaeter = wkMutation((v: { id: string }) => api.api.kasse.warenkorb[":id"].spaeter.$post({ param: { id: v.id } }));
export const useNeuerWarenkorb = wkMutation((v: { uebung?: boolean }) => api.api.kasse.warenkorb.neu.$post({ json: { uebung: !!v.uebung } }));
/** E11: Gutscheine und Mieter-Guthaben als Zahlung. */
export const useGutscheinVerkaufen = wkMutation((v: { id: string; wertCent: number }) => api.api.kasse.warenkorb[":id"].gutschein.verkaufen.$post({ param: { id: v.id }, json: { wertCent: v.wertCent } }));
export const useGutscheinEinloesen = wkMutation((v: { id: string; code: string; betragCent?: number }) => api.api.kasse.warenkorb[":id"].gutschein.einloesen.$post({ param: { id: v.id }, json: { code: v.code, betragCent: v.betragCent } }));
export const useGuthabenEinloesen = wkMutation((v: { id: string; mieternummer: string; betragCent?: number }) => api.api.kasse.warenkorb[":id"].guthaben.$post({ param: { id: v.id }, json: { mieternummer: v.mieternummer, betragCent: v.betragCent } }));
export interface GutscheinAuskunft { code: string; wertCent: number; restCent: number; status: string; gueltigBis: string | null }
export const useGutscheinAuskunft = (code: string) => useQuery({ queryKey: ["kasse", "gutschein", code], queryFn: () => rufe<GutscheinAuskunft>(api.api.kasse.gutschein[":code"].$get({ param: { code } })), enabled: code.replace(/[^A-Za-z0-9]/g, "").length >= 8, retry: false });
export interface GuthabenAuskunft { mieterId: string; name: string; mieternummer: string; guthabenCent: number; einbehalten: boolean }
export const useGuthabenAuskunft = (mieternummer: string) => useQuery({ queryKey: ["kasse", "guthaben", mieternummer], queryFn: () => rufe<GuthabenAuskunft>(api.api.kasse.guthaben[":mieternummer"].$get({ param: { mieternummer } })), enabled: /^\d{4}$/.test(mieternummer), retry: false });

/** E10.2: Offene Aufgaben für die Schicht. */
export interface KasseAufgabe {
  id: string;
  titel: string;
  text: string;
  art: string;
  faelligTag: string | null;
  zugewiesenName: string | null;
}
export const useKasseAufgaben = (aktiv: boolean) => useQuery({ queryKey: ["kasse", "aufgaben"], queryFn: () => rufe<{ aufgaben: KasseAufgabe[] }>(api.api.kasse.aufgaben.$get()), enabled: aktiv, staleTime: 60_000 });
export function useKasseAufgabeErledigt() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.kasse.aufgaben[":id"].erledigt.$post({ param: { id } })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["kasse", "aufgaben"] }) });
}
